// One entity type's slice of the sync feed: the records, the React subscription,
// and the optimistic write ops. All network plumbing (cursor, clock, pull, push)
// belongs to the SyncFeed this collection is registered with.

import type { EntityType, FieldWrite, SyncRecord } from "@/types";
import type { RecordSink, SyncFeed } from "./syncFeed";

type Listener = () => void;

/** A SyncRecord known to carry one entity's payload. `SyncRecord.fields` is a
 * union over every entity input; a collection narrows it once, here. */
export type EntityRecord<TFields> = Omit<SyncRecord, "fields"> & { fields: TFields };

const OP_UPSERT = "upsert" as const;
const OP_DELETE = "delete" as const;
const UNSAVED_VERSION = 0;

export class EntityCollection<TFields extends object> implements RecordSink {
  private records = new Map<string, EntityRecord<TFields>>();
  private listeners = new Set<Listener>();
  private snapshot: EntityRecord<TFields>[] = [];

  constructor(
    readonly entityType: EntityType,
    private readonly fieldNames: readonly (keyof TFields & string)[],
    private readonly feed: SyncFeed,
  ) {}

  // --- Read side (React: useSyncExternalStore) ---

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Live (non-tombstoned) records, newest first. Stable between emits. */
  getSnapshot = (): EntityRecord<TFields>[] => this.snapshot;

  // --- Write side (optimistic ops) ---

  /** Create a record. Throws (and rolls back) when the server refuses. */
  async create(input: TFields): Promise<void> {
    const id = crypto.randomUUID();
    const nowIso = new Date().toISOString();
    const hlc = this.feed.stamp();
    this.records.set(id, {
      id,
      entityType: this.entityType,
      version: UNSAVED_VERSION, // not on the server yet
      seq: 0,
      deleted: false,
      createdAt: nowIso,
      updatedAt: nowIso,
      fields: input,
      clocks: {},
    });
    this.emit();

    await this.feed.push(
      {
        opId: crypto.randomUUID(),
        kind: OP_UPSERT,
        entityType: this.entityType,
        id,
        baseVersion: UNSAVED_VERSION,
        set: this.fieldWrites(input, hlc),
      },
      () => this.records.delete(id),
    );
  }

  /** Edit a record; only the fields that actually changed are sent. */
  async update(id: string, changes: Partial<TFields>): Promise<void> {
    const current = this.records.get(id);
    if (!current || current.deleted) return;

    const changed = this.changedFields(current.fields, changes);
    if (Object.keys(changed).length === 0) return;

    this.records.set(id, {
      ...current,
      fields: { ...current.fields, ...changed },
      updatedAt: new Date().toISOString(),
    });
    this.emit();

    await this.feed.push(
      {
        opId: crypto.randomUUID(),
        kind: OP_UPSERT,
        entityType: this.entityType,
        id,
        baseVersion: current.version,
        set: this.fieldWrites(changed, this.feed.stamp()),
      },
      () => this.records.set(id, current),
    );
  }

  /** Delete a record (tombstone; delete wins over concurrent edits). */
  async remove(id: string): Promise<void> {
    const current = this.records.get(id);
    if (!current || current.deleted) return;

    this.records.set(id, { ...current, deleted: true });
    this.emit();

    await this.feed.push(
      {
        opId: crypto.randomUUID(),
        kind: OP_DELETE,
        entityType: this.entityType,
        id,
        baseVersion: current.version,
        set: {},
        hlc: this.feed.stamp(),
      },
      () => this.records.set(id, current),
    );
  }

  // --- RecordSink: driven by the feed ---

  /** Newer version wins; the feed is compacted so this is the full state. */
  apply(record: SyncRecord): void {
    const existing = this.records.get(record.id);
    if (!existing || record.version > existing.version) {
      this.records.set(record.id, record as EntityRecord<TFields>);
    }
  }

  /** The server accepted our op as-is: adopt the version it assigned. */
  adoptVersion(id: string, version: number, seq?: number): void {
    const record = this.records.get(id);
    if (!record) return;
    this.records.set(id, { ...record, version, seq: seq ?? record.seq });
  }

  /** Concurrent changes existed — the server's record is authoritative. */
  adoptRecord(record: SyncRecord): void {
    this.records.set(record.id, record as EntityRecord<TFields>);
  }

  /** Drop all local state (sign-out): the next user must not see this data. */
  reset(): void {
    this.records.clear();
    this.emit();
  }

  emit(): void {
    this.snapshot = [...this.records.values()]
      .filter((record) => !record.deleted)
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    for (const listener of this.listeners) listener();
  }

  // --- Field helpers ---

  /** Build the op `set` map for the given fields, all stamped with one clock. */
  private fieldWrites(fields: Partial<TFields>, hlc: string): Record<string, FieldWrite> {
    const set: Record<string, FieldWrite> = {};
    for (const name of this.fieldNames) {
      const value = fields[name];
      if (value !== undefined) set[name] = { value, hlc } as FieldWrite;
    }
    return set;
  }

  private changedFields(current: TFields, changes: Partial<TFields>): Partial<TFields> {
    const changed: Partial<TFields> = {};
    for (const name of this.fieldNames) {
      const value = changes[name];
      if (value !== undefined && value !== current[name]) changed[name] = value;
    }
    return changed;
  }
}
